import { AsyncPipe } from "@angular/common";
import { Component, inject, input } from "@angular/core";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { ActivatedRouteSnapshot, RouterLink } from "@angular/router";
import { defaultBreadcrumbDisplay } from "@lib/base/components/navigation/breadcrumb/breadcrumb.utils";
import { WindowLayoutDirective } from "@lib/base/directives/window-layout.directive";
import { ResizeWindowService } from "@lib/base/services/resize-window.service";
import { RoutingService } from "@lib/base/services/routing.service";
import { notNull } from "@lib/base/utils/object.utils";
import { getAllSnapshots } from "@lib/base/utils/routing.utils";
import { TranslatePipe } from "@ngx-translate/core";
import { map, Observable } from "rxjs";

export type BreadcrumbDisplayFunctions = (snapshot: ActivatedRouteSnapshot, url: string) => Array<BreadcrumbItem> | null;

export type BreadcrumbItem = {
    label: string;
    route: string | null;
};

type BreadcrumbItemWithKey = {
    label: string;
    route: string | null;
    key: string;
};

@Component({
    selector: "app-breadcrumb",
    imports: [
        AsyncPipe,
        MatIcon,
        RouterLink,
        TranslatePipe,
        MatButton,
        MatMenuItem,
        MatMenuTrigger,
        MatMenu,
    ],
    templateUrl: "./breadcrumb.component.html",
    styleUrl: "./breadcrumb.component.scss",
    hostDirectives: [WindowLayoutDirective],
})
export class BreadcrumbComponent {
    private routingService = inject(RoutingService);
    resizeWindowService = inject(ResizeWindowService);

    displayFunction = input<BreadcrumbDisplayFunctions>();

    protected url: Observable<BreadcrumbItemWithKey[]> = this.routingService.routingEvents.pipe(
        map((activeAppRoute) => {
            return getAllSnapshots(activeAppRoute.activatedRoute.snapshot)
                .flatMap((snapshot) => {
                    const customDisplay = this.displayFunction()?.apply(null, [snapshot, activeAppRoute.url]);
                    if (customDisplay != null) return customDisplay;
                    return defaultBreadcrumbDisplay(snapshot, activeAppRoute.url);
                })
                .filter(notNull) ?? [];
        }),
        map((elements) => {
            return elements.map((el, i) => ({
                ...el,
                key: el.route ?? i.toString(),
            }));
        }),
    );

    protected current = this.url.pipe(
        map(url => url.at(-1)),
    );
}
